import React, { useState } from 'react';
import { ChevronDown, Zap, Percent, Coins } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import EditablePill from './EditablePill';
import BreakupChart from './BreakupChart';
import { formatMoney, parseMoneyInput, currencies } from '../lib/currency';
import { useAppSettings } from '../lib/i18n';

/**
 * Combined Credit Days + Interest Subvention panel — compact layout.
 * When either is enabled we render:
 *   - a horizontal 3-card stat row (Effective ROI · Revised EMI · Interest saved)
 *   - a 2-up mini chart row (Actual breakup  vs  Effect of advance options)
 *     using the SAME chart type as the primary chart.
 */
export default function AdvancedSection({
  advanced,
  setAdvanced,
  currentSimulation,     // WITH advanced
  plainSimulation,       // WITHOUT advanced (for comparison)
  loanAmount,
  interestRate,
  chartType,
  moratoriumEnabled,
  moratoriumType,
}) {
  const { currency } = useAppSettings();
  const [open, setOpen] = useState(false);

  const upd = (patch) => setAdvanced({ ...advanced, ...patch });

  const eitherOn = advanced.creditDaysEnabled || advanced.subventionEnabled;
  const lockedOpen = eitherOn; // an active option keeps the section pinned open
  const effectiveOpen = lockedOpen || open;
  const emiBase = plainSimulation?.summary?.emi || 0;
  const emiNew = currentSimulation?.summary?.emi || 0;
  const emiDelta = Math.max(0, emiBase - emiNew);
  const interestSaved = Math.max(0, (plainSimulation?.summary?.totalInterest || 0) - (currentSimulation?.summary?.totalInterest || 0));
  const effRate = Number(currentSimulation?.summary?.effectiveAnnualRate || 0);

  const dRateDisplay = (advanced.discountingRate == null || advanced.discountingRate === '')
    ? Number(interestRate) || 0
    : Number(advanced.discountingRate);

  return (
    <section className="emi-wrap mt-8">
      <div className="rounded-2xl border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#101B33] overflow-hidden">
        <button
          type="button"
          onClick={() => { if (!lockedOpen) setOpen((v) => !v); }}
          className={`w-full flex items-center justify-between p-4 transition-colors ${lockedOpen ? 'cursor-default' : 'hover:bg-slate-50/60 dark:hover:bg-[#1B2743]/60'}`}
          data-testid="advanced-toggle"
          aria-expanded={effectiveOpen}
        >
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#0EA5FF] to-[#0176D3] text-white flex items-center justify-center shadow-sm">
              <Zap className="h-4 w-4" />
            </div>
            <div className="text-left">
              <div className="text-base font-semibold text-[#032D60] dark:text-slate-100">Advanced options</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Credit days · Interest subvention — enable either or both to lower your effective ROI &amp; EMI
              </div>
            </div>
          </div>
          {lockedOpen ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF5] dark:bg-[#0B2A1D] border border-[#A7F3D0] dark:border-[#0F5D42] px-2.5 py-1 text-[10px] font-bold text-[#059669] dark:text-emerald-300 uppercase tracking-wide" data-testid="advanced-active-badge">
              Active
            </span>
          ) : (
            <ChevronDown className={`h-5 w-5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          )}
        </button>

        <div className="overflow-hidden transition-all" style={{ maxHeight: effectiveOpen ? '3000px' : '0px' }}>
          <div className="border-t border-slate-100 dark:border-[#1E2A44] p-4 sm:p-5 space-y-4">

            {/* Two compact toggle cards side-by-side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* CREDIT DAYS */}
              <div className="rounded-lg bg-white dark:bg-[#0F1B36] border border-[#B7E1FA] dark:border-[#1E4A72] p-3">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="credit-days-toggle">
                  <Checkbox
                    checked={advanced.creditDaysEnabled}
                    onCheckedChange={(v) => upd({ creditDaysEnabled: Boolean(v) })}
                    className="h-4 w-4 border-2 border-[#0176D3] data-[state=checked]:bg-[#0176D3] data-[state=checked]:border-[#0176D3]"
                  />
                  <span className="h-6 w-6 rounded-md flex items-center justify-center bg-[#EAF5FE] text-[#0176D3] border border-[#B7E1FA] shrink-0">
                    <Percent className="h-3 w-3" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-[#032D60] dark:text-slate-100">Credit days</div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug truncate">
                      Grace period from seller · discount off effective principal
                    </div>
                  </div>
                </label>
                {advanced.creditDaysEnabled && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between gap-2 rounded-md bg-[#F5FBFF] dark:bg-[#0F1B36]/40 border border-[#B7E1FA] dark:border-[#1E4A72] px-2 py-1">
                      <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 uppercase">Days</span>
                      <EditablePill
                        value={advanced.creditDays}
                        onChange={(v) => upd({ creditDays: v })}
                        min={0}
                        max={365}
                        format={(v) => `${v}`}
                        parse={(s) => Math.round(Number(String(s).replace(/[^\d.]/g, '')))}
                        width="w-14"
                        size="sm"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2 rounded-md bg-[#F5FBFF] dark:bg-[#0F1B36]/40 border border-[#B7E1FA] dark:border-[#1E4A72] px-2 py-1">
                      <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 uppercase">Rate</span>
                      <EditablePill
                        value={dRateDisplay}
                        onChange={(v) => upd({ discountingRate: Number(Number(v).toFixed(2)) })}
                        min={0}
                        max={50}
                        format={(v) => Number(v).toFixed(2)}
                        parse={(s) => Number(String(s).replace(/[^\d.]/g, ''))}
                        suffix="%"
                        width="w-16"
                        size="sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* SUBVENTION */}
              <div className="rounded-lg bg-white dark:bg-[#0F1B36] border border-[#A7F3D0] dark:border-[#0F5D42] p-3">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="subvention-toggle">
                  <Checkbox
                    checked={advanced.subventionEnabled}
                    onCheckedChange={(v) => upd({ subventionEnabled: Boolean(v) })}
                    className="h-4 w-4 border-2 border-[#059669] data-[state=checked]:bg-[#059669] data-[state=checked]:border-[#059669]"
                  />
                  <span className="h-6 w-6 rounded-md flex items-center justify-center bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0] shrink-0">
                    <Coins className="h-3 w-3" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-[#032D60] dark:text-slate-100">Interest subvention</div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug truncate">
                      One-time upfront discount from seller · reduces effective principal
                    </div>
                  </div>
                </label>
                {advanced.subventionEnabled && (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-[#F0FDF4] dark:bg-[#0F1B36]/40 border border-[#A7F3D0] dark:border-[#0F5D42] px-2 py-1">
                    <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 uppercase">Amount</span>
                    <EditablePill
                      value={advanced.subventionAmount}
                      onChange={(v) => upd({ subventionAmount: v })}
                      min={0}
                      max={100000000}
                      format={(v) => formatMoney(v, currency)}
                      parse={parseMoneyInput}
                      width="w-32"
                      size="sm"
                      tone="green"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Effect row (only when either is enabled) */}
            {eitherOn && (
              <>
                {/* Stat cards — 3 side by side, compact */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="effective-panel">
                  <MiniStat label="Effective ROI" value={`${effRate.toFixed(2)}%`} sub={`vs ${Number(interestRate).toFixed(2)}%`} tone="blue" testid="effective-roi" />
                  <MiniStat label="Revised EMI" value={formatMoney(emiNew, currency)} sub={emiDelta > 0 ? `saves ${formatMoney(emiDelta, currency)}/mo` : `vs ${formatMoney(emiBase, currency)}`} tone="green" testid="revised-emi" />
                  <MiniStat label="Interest saved" value={formatMoney(interestSaved, currency)} sub={interestSaved > 0 ? 'over full tenure' : 'no change'} tone="teal" testid="interest-saved" />
                </div>

                {/* Two side-by-side mini breakup charts */}
                <div className="grid grid-cols-2 gap-3">
                  <MiniChartCard
                    title="Actual Representation"
                    subtitle="Without advance options"
                    principal={loanAmount}
                    interest={plainSimulation?.summary?.totalInterest || 0}
                    emi={plainSimulation?.summary?.emi || 0}
                    roi={Number(interestRate)}
                    moratoriumInterest={moratoriumEnabled && moratoriumType === 'full' ? (plainSimulation?.summary?.moratoriumInterestCapitalised || 0) : 0}
                    schedule={plainSimulation?.schedule || []}
                    chartType={chartType}
                    chartId="advanced-actual-chart"
                    currency={currency}
                  />
                  <MiniChartCard
                    title="With Advance Options"
                    subtitle="Effect of your inputs"
                    principal={loanAmount}
                    interest={currentSimulation?.summary?.totalInterest || 0}
                    emi={currentSimulation?.summary?.emi || 0}
                    roi={effRate}
                    moratoriumInterest={moratoriumEnabled && moratoriumType === 'full' ? (currentSimulation?.summary?.moratoriumInterestCapitalised || 0) : 0}
                    schedule={currentSimulation?.schedule || []}
                    chartType={chartType}
                    chartId="advanced-revised-chart"
                    currency={currency}
                    highlight
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, value, sub, tone = 'blue', testid }) {
  const tones = {
    blue: 'bg-gradient-to-br from-[#EAF5FE] to-white dark:from-[#0F1B36] dark:to-[#1E3A5F] border-[#B7E1FA] dark:border-[#1E4A72]',
    green: 'bg-gradient-to-br from-[#ECFDF5] to-white dark:from-[#0F1B36] dark:to-[#10362A] border-[#A7F3D0] dark:border-[#0F5D42]',
    teal: 'bg-gradient-to-br from-[#F0FDFA] to-white dark:from-[#0F1B36] dark:to-[#0F5D5D] border-[#99F6E4] dark:border-[#0F5D5D]',
  }[tone];
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide font-bold text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-0.5 text-lg font-bold tabular-nums text-[#032D60] dark:text-white leading-tight">{value}</div>
      <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{sub}</div>
    </div>
  );
}

export function MiniChartCard({ title, subtitle, principal, interest, emi, roi, moratoriumInterest = 0, schedule = [], chartType = 'donut', chartId, currency = 'INR', highlight }) {
  const size = chartType === 'donut' ? 240 : 250;
  const showROI = roi != null && !isNaN(Number(roi));
  return (
    <div className={`rounded-lg border ${highlight ? 'border-[#0176D3] shadow-[0_2px_10px_rgba(1,118,211,0.15)]' : 'border-slate-200 dark:border-[#1E2A44]'} bg-white dark:bg-[#0F1B36] p-3`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-sm font-bold text-[#032D60] dark:text-slate-100 truncate">{title}</div>
        <div className="text-[9px] uppercase tracking-wide text-slate-400 shrink-0 ml-2">{chartType}</div>
      </div>
      {subtitle && <div className="text-[10px] text-slate-500 dark:text-slate-400 mb-1 truncate">{subtitle}</div>}
      <div className="flex items-center gap-4">
        <div className="shrink-0">
          <BreakupChart
            type={chartType}
            principal={principal}
            interest={interest}
            moratoriumInterest={moratoriumInterest}
            schedule={schedule}
            size={size}
            chartId={chartId}
          />
        </div>
        <div className="flex-1 min-w-0 space-y-2.5">
          <SideStat swatch="#0176D3" label="Principal" value={formatMoney(principal, currency)} />
          <SideStat swatch="#F97066" label="Interest" value={formatMoney(interest, currency)} valueClass="text-[#B91C1C] dark:text-red-300" />
          <SideStat swatch="#0EA5FF" label="EMI" value={formatMoney(emi, currency)} valueClass="text-[#0176D3] dark:text-sky-300" />
          {showROI && (
            <SideStat swatch="#059669" label="ROI" value={`${Number(roi).toFixed(2)}%`} valueClass="text-[#059669] dark:text-emerald-300" />
          )}
        </div>
      </div>
    </div>
  );
}

function SideStat({ swatch, label, value, valueClass }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-3 w-3 rounded-sm shrink-0" style={{ backgroundColor: swatch }} />
      <span className="text-xs text-slate-500 dark:text-slate-400 w-16 shrink-0">{label}</span>
      <span className={`text-base font-bold tabular-nums ${valueClass || 'text-[#032D60] dark:text-white'}`}>{value}</span>
    </div>
  );
}
